import { useEffect, useState } from 'react';
import { api, money } from '../api.js';
import { ReportHead, Money, Empty } from '../components/Shell.jsx';
import { blankLine, SIDES, isDisabled, withAmount, summarise, toApiLines } from '../lib/journalLines.js';

const today = () => new Date().toISOString().slice(0, 10);

function useChart(entityId) {
  const [accounts, setAccounts] = useState([]);
  const [taxCodes, setTaxCodes] = useState([]);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    if (!entityId) return;
    Promise.all([
      api.get(`/api/entities/${entityId}/accounts`),
      api.get(`/api/entities/${entityId}/tax-codes`),
      api.get(`/api/entities/${entityId}/counterparties`),
    ]).then(([a, t, c]) => {
      setAccounts(a.accounts.filter((x) => x.is_postable));
      setTaxCodes(t.taxCodes);
      setContacts(c.counterparties);
    }).catch(() => {});
  }, [entityId]);

  return { accounts, taxCodes, contacts };
}

/* --------------------------------------------------------------- journal */
/**
 * A journal, written the way a journal is written: two money columns, added
 * separately, agreeing at the foot.
 *
 * IT USED TO BE ONE SIGNED COLUMN, subtitled "Debits positive, credits
 * negative". The balance rule underneath it was real — and still is, unchanged
 * and in two places, here and in postJournal — but a bookkeeper reading a
 * screen with one amount column has nothing to check it against. There was no
 * debit total, no credit total, and nothing to compare, so the one thing the
 * screen was certain about was the one thing it never showed.
 *
 * So the columns are the change, and the totals in the <tfoot> are the point of
 * it. The gate is exactly the gate it was: `ready` still refuses to enable Post
 * until the entry balances, and the API still refuses an entry that does not.
 * Nothing new is enforced here — the existing verdict is simply legible.
 *
 * The arithmetic lives in lib/journalLines.js, in integer cents, and the
 * conversion to the API's single signed amount happens there too. This file
 * does no money arithmetic at all.
 */
export function JournalEntry({ entity }) {
  const { accounts } = useChart(entity?.id);
  const [date, setDate] = useState(today());
  const [description, setDescription] = useState('');
  const [lines, setLines] = useState([blankLine(), blankLine()]);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const sum = summarise(lines);

  const submit = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await api.post(`/api/entities/${entity.id}/journals`, {
        transactionDate: date,
        description,
        // The only place the two columns become the API's signed amount.
        lines: toApiLines(lines),
      });
      setMsg(`Posted as entry ${r.entry.entryNo}.`);
      setLines([blankLine(), blankLine()]);
      setDescription('');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  // The same gate as before — balanced, described, at least two usable lines —
  // with the addition that a line the payload would silently drop (a figure
  // with no account, an amount that is not a figure) holds the post back
  // rather than reaching the server as a 422.
  const ready = sum.balanced && !sum.problem && description.trim() && sum.postable >= 2;

  const set = (i, patch) => setLines(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  return (
    <>
      <ReportHead entity={entity} title="Journal entry"
                  sub="Each line is a debit or a credit. The entry posts only when the two columns agree." />
      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      <div className="form-grid">
        <label className="field"><span>Date</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        <label className="field" style={{ gridColumn: 'span 2' }}><span>Description</span>
          <input value={description} onChange={(e) => setDescription(e.target.value)}
                 placeholder="What this entry records" /></label>
      </div>

      <div className="scroll-x">
        {/* The ruled ledger, with the inputs inside it: the row being typed is
            the row being read, and the two money columns carry the same blue
            and oxblood rules they carry on the trial balance. */}
        <table className="ledger ledger--grid">
          <thead>
            <tr>
              <th style={{ width: 260 }}>Account</th>
              <th>Narration</th>
              <th className="num col-debit" style={{ width: 140 }}>Debit</th>
              <th className="num col-credit" style={{ width: 140 }}>Credit</th>
              <th style={{ width: 96 }} />
            </tr>
          </thead>

          <tbody>
            {lines.map((line, i) => (
              <tr key={i}>
                <td>
                  <select value={line.accountId} aria-label={`Account, line ${i + 1}`}
                          onChange={(e) => set(i, { accountId: e.target.value })}>
                    <option value="">Choose an account</option>
                    {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                  </select>
                </td>
                <td>
                  <input value={line.description ?? ''} placeholder="Optional"
                         aria-label={`Narration, line ${i + 1}`}
                         onChange={(e) => set(i, { description: e.target.value })} />
                </td>
                {SIDES.map((side) => (
                  // Typing in one column clears the other and closes it. A line
                  // is a debit or a credit; it is never both, and the form
                  // cannot be made to hold one that is.
                  <td className={`num col-${side}`} key={side}>
                    <input className="num input--figure" inputMode="decimal"
                           placeholder={isDisabled(line, side) ? '' : '0.00'}
                           aria-label={`${side === 'debit' ? 'Debit' : 'Credit'}, line ${i + 1}`}
                           value={line[side]}
                           disabled={isDisabled(line, side)}
                           onChange={(e) => setLines(withAmount(lines, i, side, e.target.value))} />
                  </td>
                ))}
                <td>
                  <button className="icon-btn icon-btn--tight" aria-label={`Remove line ${i + 1}`}
                          onClick={() => setLines(lines.length > 2 ? lines.filter((_, j) => j !== i) : lines)}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>

          {/* The foot of a journal page, and the whole reason for the columns:
              each one added on its own, so that "they agree" is something the
              eye can check rather than something the button asserts. */}
          <tfoot>
            <tr>
              <td colSpan={2}>Totals</td>
              <td className="num col-debit figure">{money(sum.debit)}</td>
              <td className="num col-credit figure">{money(sum.credit)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button className="btn btn--quiet" onClick={() => setLines([...lines, blankLine()])}>
          Add line
        </button>
        <button className="btn" onClick={submit} disabled={!ready || busy}>
          {busy ? 'Posting' : 'Post entry'}
        </button>
      </div>

      {sum.problem && <div className="note note--warn">{sum.problem}</div>}

      <div className={`balance${sum.balanced ? '' : ' balance--off'}`}>
        <span className="balance__state">{sum.balanced ? 'Balanced' : 'Out of balance'}</span>
        <span className="balance__detail">
          {sum.balanced
            // The promise is made only when it is true. The columns agreeing is
            // one of the conditions on Post, not all of them — and now that the
            // totals are on screen beside a greyed-out button, a balance rule
            // that says "can be posted" regardless is a contradiction the
            // reader can see.
            ? <>Debits equal credits.{ready ? <> This entry can be posted.</> : null}</>
            : <>Difference <strong>{money(sum.difference)}</strong> — adjust a line before posting.</>}
        </span>
      </div>
    </>
  );
}

/* ---------------------------------------------------------------- invoice */
export function Invoice({ entity }) {
  const { accounts, taxCodes, contacts } = useChart(entity?.id);
  const [type, setType] = useState('sales_invoice');
  const [counterpartyId, setCounterpartyId] = useState('');
  const [date, setDate] = useState(today());
  const [lines, setLines] = useState([{ description: '', accountId: '', taxCodeId: '', unitPrice: '' }]);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const isSales = type.startsWith('sales');
  const wanted = isSales ? 'customer' : 'supplier';
  const eligible = contacts.filter((c) => c.type === wanted);

  useEffect(() => { setCounterpartyId(''); }, [type]);

  const submit = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await api.post(`/api/entities/${entity.id}/documents`, {
        type, counterpartyId, documentDate: date,
        lines: lines.filter((l) => l.accountId && l.unitPrice !== '').map((l) => ({
          description: l.description || 'Services',
          accountId: l.accountId,
          taxCodeId: l.taxCodeId || undefined,
          unitPrice: l.unitPrice,
        })),
      });
      setMsg(`${r.document.number} posted — net ${money(r.document.net)}, VAT ${money(r.document.vat)}, total ${money(r.document.gross)}.`);
      setLines([{ description: '', accountId: '', taxCodeId: '', unitPrice: '' }]);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const ready = counterpartyId && lines.some((l) => l.accountId && l.unitPrice !== '');
  const deniedUsed = lines.some((l) => taxCodes.find((t) => t.id === l.taxCodeId)?.is_input_denied);

  return (
    <>
      <ReportHead entity={entity} title="Tax invoice"
                  sub="Prices are entered excluding VAT. VAT is computed at the rate effective on the document date." />
      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      <div className="form-grid">
        <label className="field"><span>Document</span>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="sales_invoice">Sales — tax invoice</option>
            <option value="sales_credit_note">Sales — credit note</option>
            <option value="purchase_invoice">Purchase — supplier invoice</option>
            <option value="purchase_credit_note">Purchase — supplier credit note</option>
          </select></label>
        <label className="field"><span>{isSales ? 'Customer' : 'Supplier'}</span>
          <select value={counterpartyId} onChange={(e) => setCounterpartyId(e.target.value)}>
            <option value="">Choose a {wanted}</option>
            {eligible.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
          </select></label>
        <label className="field"><span>Date</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
      </div>

      {eligible.length === 0 && (
        <div className="note note--warn">
          No {wanted}s yet. Add one under Customers &amp; suppliers before raising this document.
        </div>
      )}

      {lines.map((line, i) => (
        <div className="line-row" key={i}>
          <label className="field"><span>{i === 0 ? 'Description' : ''}</span>
            <input value={line.description} placeholder="What was supplied"
                   onChange={(e) => setLines(lines.map((l, j) => j === i ? { ...l, description: e.target.value } : l))} /></label>
          <label className="field"><span>{i === 0 ? 'Account' : ''}</span>
            <select value={line.accountId}
                    onChange={(e) => setLines(lines.map((l, j) => j === i ? { ...l, accountId: e.target.value } : l))}>
              <option value="">Choose an account</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
            </select></label>
          <label className="field"><span>{i === 0 ? 'VAT' : ''}</span>
            <select value={line.taxCodeId}
                    onChange={(e) => setLines(lines.map((l, j) => j === i ? { ...l, taxCodeId: e.target.value } : l))}>
              <option value="">No VAT</option>
              {taxCodes.filter((t) => isSales ? t.direction === 'output' : t.direction !== 'output')
                .map((t) => <option key={t.id} value={t.id}>{t.code} — {t.name}</option>)}
            </select></label>
          <label className="field"><span>{i === 0 ? 'Amount excl.' : ''}</span>
            <input className="input--figure" inputMode="decimal" value={line.unitPrice} placeholder="0.00"
                   onChange={(e) => setLines(lines.map((l, j) => j === i ? { ...l, unitPrice: e.target.value } : l))} /></label>
          <button className="icon-btn" aria-label={`Remove line ${i + 1}`}
                  onClick={() => setLines(lines.length > 1 ? lines.filter((_, j) => j !== i) : lines)}>Remove</button>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button className="btn btn--quiet"
                onClick={() => setLines([...lines, { description: '', accountId: '', taxCodeId: '', unitPrice: '' }])}>
          Add line
        </button>
        <button className="btn" onClick={submit} disabled={!ready || busy}>
          {busy ? 'Posting' : 'Post document'}
        </button>
      </div>

      {deniedUsed && (
        <div className="note note--warn">
          A VAT code on this document denies the input claim under section 17(2) —
          entertainment, motor cars or club subscriptions. The VAT will be posted to the
          expense instead of claimed, and disclosed separately on the VAT report.
        </div>
      )}
    </>
  );
}

/* --------------------------------------------------------------- payment */
export function Payment({ entity }) {
  const { accounts, contacts } = useChart(entity?.id);
  const [direction, setDirection] = useState('receipt');
  const [counterpartyId, setCounterpartyId] = useState('');
  const [date, setDate] = useState(today());
  const [amount, setAmount] = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [reference, setReference] = useState('');
  const [open, setOpen] = useState([]);
  const [allocations, setAllocations] = useState({});
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const wanted = direction === 'receipt' ? 'customer' : 'supplier';
  const banks = accounts.filter((a) => a.control_kind === 'bank' || a.code === '1750');

  useEffect(() => { if (banks.length && !bankAccountId) setBankAccountId(banks[0].id); }, [accounts]);
  useEffect(() => { setCounterpartyId(''); setOpen([]); setAllocations({}); }, [direction]);

  useEffect(() => {
    if (!counterpartyId) { setOpen([]); return; }
    api.get(`/api/entities/${entity.id}/documents?counterpartyId=${counterpartyId}&unpaidOnly=true`)
      .then((d) => setOpen(d.documents)).catch(() => setOpen([]));
  }, [counterpartyId]);

  const submit = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      const allocs = Object.entries(allocations)
        .filter(([, v]) => v && Number(v) > 0)
        .map(([documentId, v]) => ({ documentId, amount: v }));
      const r = await api.post(`/api/entities/${entity.id}/payments`, {
        counterpartyId, direction, paymentDate: date, amount,
        bankAccountId, reference: reference || undefined, allocations: allocs,
      });
      setMsg(`Posted as entry ${r.payment.entryNo}.`);
      setAmount(''); setReference(''); setAllocations({}); setCounterpartyId('');
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <>
      <ReportHead entity={entity} title={direction === 'receipt' ? 'Receipt' : 'Payment'}
                  sub="Allocate against open invoices. Anything unallocated stays on account." />
      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      <div className="form-grid">
        <label className="field"><span>Type</span>
          <select value={direction} onChange={(e) => setDirection(e.target.value)}>
            <option value="receipt">Receipt from a customer</option>
            <option value="payment">Payment to a supplier</option>
          </select></label>
        <label className="field"><span>{direction === 'receipt' ? 'Customer' : 'Supplier'}</span>
          <select value={counterpartyId} onChange={(e) => setCounterpartyId(e.target.value)}>
            <option value="">Choose a {wanted}</option>
            {contacts.filter((c) => c.type === wanted)
              .map((c) => <option key={c.id} value={c.id}>{c.code} — {c.name}</option>)}
          </select></label>
        <label className="field"><span>Date</span>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        <label className="field"><span>Amount</span>
          <input className="input--figure" inputMode="decimal" value={amount} placeholder="0.00"
                 onChange={(e) => setAmount(e.target.value)} /></label>
        <label className="field"><span>Bank account</span>
          <select value={bankAccountId} onChange={(e) => setBankAccountId(e.target.value)}>
            {banks.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
          </select></label>
        <label className="field"><span>Reference</span>
          <input value={reference} placeholder="EFT number" onChange={(e) => setReference(e.target.value)} /></label>
      </div>

      {counterpartyId && (open.length === 0 ? (
        <div className="note">No open documents. This will be recorded on account.</div>
      ) : (
        <div className="scroll-x">
          <table className="ledger">
            <thead>
              <tr><th>Document</th><th>Date</th><th className="num">Total</th>
                  <th className="num">Outstanding</th><th className="num" style={{ width: 150 }}>Allocate</th></tr>
            </thead>
            <tbody>
              {open.map((d) => (
                <tr key={d.id}>
                  <td className="figure">{d.number}</td>
                  <td className="figure">{d.document_date}</td>
                  <td className="num"><Money value={d.gross} /></td>
                  <td className="num"><Money value={d.outstanding} /></td>
                  <td className="num">
                    <input className="input--figure" style={{ width: 130, textAlign: 'right' }}
                           inputMode="decimal" placeholder="0.00"
                           value={allocations[d.id] ?? ''}
                           onChange={(e) => setAllocations({ ...allocations, [d.id]: e.target.value })} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <button className="btn" style={{ marginTop: 14 }} onClick={submit}
              disabled={busy || !counterpartyId || !amount || !bankAccountId}>
        {busy ? 'Posting' : `Post ${direction}`}
      </button>
    </>
  );
}

/* -------------------------------------------------------------- contacts */
export function Contacts({ entity }) {
  const [contacts, setContacts] = useState([]);
  const [form, setForm] = useState({ type: 'customer', code: '', name: '', vatNumber: '', email: '', paymentTermsDays: 30 });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/api/entities/${entity.id}/counterparties`)
    .then((d) => setContacts(d.counterparties)).catch(() => {});

  useEffect(() => { if (entity?.id) load(); }, [entity?.id]);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post(`/api/entities/${entity.id}/counterparties`, {
        ...form, paymentTermsDays: Number(form.paymentTermsDays),
        vatNumber: form.vatNumber || undefined, email: form.email || undefined,
      });
      setForm({ ...form, code: '', name: '', vatNumber: '', email: '' });
      load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <>
      <ReportHead entity={entity} title="Customers & suppliers" />
      {err && <div className="error">{err}</div>}

      <div className="form-grid">
        <label className="field"><span>Type</span>
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            <option value="customer">Customer</option><option value="supplier">Supplier</option>
          </select></label>
        <label className="field"><span>Code</span>
          <input value={form.code} placeholder="ACME001" onChange={(e) => setForm({ ...form, code: e.target.value })} /></label>
        <label className="field"><span>Name</span>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
        <label className="field"><span>VAT number</span>
          <input className="input--figure" value={form.vatNumber}
                 onChange={(e) => setForm({ ...form, vatNumber: e.target.value })} /></label>
        <label className="field"><span>Email</span>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
        <label className="field"><span>Terms (days)</span>
          <input className="input--figure" type="number" value={form.paymentTermsDays}
                 onChange={(e) => setForm({ ...form, paymentTermsDays: e.target.value })} /></label>
      </div>
      <button className="btn" onClick={submit} disabled={busy || !form.code || !form.name}>
        {busy ? 'Saving' : 'Add contact'}
      </button>

      <div style={{ marginTop: 26 }} className="scroll-x">
        {contacts.length === 0 ? <Empty><p>No customers or suppliers yet.</p></Empty> : (
          <table className="ledger">
            <thead><tr><th>Code</th><th>Name</th><th>Type</th><th>VAT number</th><th className="num">Terms</th></tr></thead>
            <tbody>
              {contacts.map((c) => (
                <tr key={c.id}>
                  <td className="code figure">{c.code}</td>
                  <td>{c.name}</td>
                  <td>{c.type}</td>
                  <td className="figure">{c.vat_number ?? '—'}</td>
                  <td className="num figure">{c.payment_terms_days}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
